/**
 * Adapts the web questionnaire answer shape to the structured tech-stack model. The
 * Current Technology Stack section is now stored as one structured `tech_stack_systems`
 * answer (an array of systems); this reads + parses it for the read-only landscape viz.
 */
import {
  parseTechStackSystems,
  seedTechStackSystemsFromLegacy,
  type TechStackSystem,
} from '@protoss/shared';
import {
  getAnswerValueByFieldKey,
  type QuestionnaireAnswerMap,
  type QuestionnaireFieldLookup,
} from '../../../features/sales/utils/questionnaire-field-visibility';

export const TECH_STACK_SYSTEMS_FIELD_KEY = 'tech_stack_systems';

export function buildTechStackSystemsFromAnswers(
  answers: QuestionnaireAnswerMap,
  fieldLookup?: QuestionnaireFieldLookup
): TechStackSystem[] {
  const raw = getAnswerValueByFieldKey(TECH_STACK_SYSTEMS_FIELD_KEY, answers, fieldLookup);
  return parseTechStackSystems(raw);
}

/**
 * Seed starter systems from the legacy per-category `tech_stack_<cat>_tool`/`_plan` answers
 * (which AI discovery populates) - used to scaffold the structured editor the first time.
 */
export function seedTechStackSystemsFromAnswers(
  answers: QuestionnaireAnswerMap,
  fieldLookup?: QuestionnaireFieldLookup
): TechStackSystem[] {
  return seedTechStackSystemsFromLegacy({
    getRawValue: (fieldKey) => getAnswerValueByFieldKey(fieldKey, answers, fieldLookup),
    getOptionLabel: (fieldKey, value) =>
      fieldLookup?.get(fieldKey)?.options?.find((opt) => opt.value === value)?.label,
  });
}

/**
 * Systems to DISPLAY: the saved structured answer, or - when empty - proposals seeded from the
 * legacy AI-detected tool/plan answers. Shared by the editor AND the read-only landscape so both
 * resolve the SAME systems; without the seed fallback the diagram renders empty for an AI-filled
 * deal until the first manual edit materializes the structured answer. Pure/synchronous, so any
 * consumer memoizing on `answers` updates live.
 */
export function resolveTechStackSystemsFromAnswers(
  answers: QuestionnaireAnswerMap,
  fieldLookup?: QuestionnaireFieldLookup
): TechStackSystem[] {
  const saved = buildTechStackSystemsFromAnswers(answers, fieldLookup);
  return saved.length > 0 ? saved : seedTechStackSystemsFromAnswers(answers, fieldLookup);
}
